import { useState, useEffect } from "react";
import ModalBackButton from "../components/ModalBackButton";
import { SectionTitle } from "../components/SectionTitle";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { glideLocalStorage } from "../lib/glideLocalStorage";
import { logger, LogEntry } from "../lib/logger";
import memStore from "../lib/memStore";
import { useDeviceContext } from "../lib/useDeviceContext";
import { useGlideDepositConfig } from "../lib/useDepositConfig";

export function DebugStep({ onBack }: { onBack: () => void }) {
  const { isMobile } = useDeviceContext();
  const depositConfig = useGlideDepositConfig();
  const [glideKeys, setGlideKeys] = useState<{ key: string; value: string }[]>(
    [],
  );
  const [sessionKeys, setSessionKeys] = useState<
    { key: string; value: string }[]
  >([]);
  const [memStoreKeys, setMemStoreKeys] = useState<
    { key: string; value: string }[]
  >([]);
  const [glideStorageKeys, setGlideStorageKeys] = useState<
    { key: string; value: string }[]
  >([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  const loadLocalStorageData = () => {
    // Get all keys from localStorage that start with "glide." or include "glide.wc"
    const keys = Object.keys(window.localStorage).filter(
      (key) => key.startsWith("glide.") || key.includes("glide.wc"),
    );

    // Get the values for each key
    const keyValuePairs = keys.map((key) => ({
      key,
      value: window.localStorage.getItem(key) || "",
    }));

    setGlideKeys(keyValuePairs);
  };

  const loadSessionStorageData = () => {
    // Get all keys from sessionStorage
    const keys = Object.keys(window.sessionStorage);

    // Get the values for each key
    const keyValuePairs = keys.map((key) => ({
      key,
      value: window.sessionStorage.getItem(key) || "",
    }));

    setSessionKeys(keyValuePairs);
  };

  const loadMemStoreData = () => {
    // Get all keys from memStore
    const keys = memStore.getKeys();

    // Get the values for each key
    const keyValuePairs = keys.map((key) => ({
      key,
      value: memStore.getItem(key) || "",
    }));

    setMemStoreKeys(keyValuePairs);
  };

  const loadGlideStorageData = async () => {
    // Get all keys from glideLocalStorage
    const keys = await glideLocalStorage.getKeys();

    // Get the values for each key
    const keyValuePairs = await Promise.all(
      keys.map(async (key) => ({
        key,
        value: (await glideLocalStorage.getItem(key)) || "",
      })),
    );

    setGlideStorageKeys(keyValuePairs);
  };

  const clearLocalStorage = () => {
    // Clear only Glide-related items from localStorage
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith("glide.") || key.includes("glide.wc"))
      .forEach((key) => window.localStorage.removeItem(key));

    // Refresh the displayed data
    loadLocalStorageData();
  };

  const clearSessionStorage = () => {
    // Clear all session storage
    window.sessionStorage.clear();

    // Refresh the displayed data
    loadSessionStorageData();
  };

  const clearMemStore = () => {
    // Clear all memStore data
    memStore.clear();

    // Refresh the displayed data
    loadMemStoreData();
  };

  const clearGlideStorage = async () => {
    // Get all keys and remove each item
    const keys = await glideLocalStorage.getKeys();
    for (const key of keys) {
      await glideLocalStorage.removeItem(key);
    }

    // Refresh the displayed data
    loadGlideStorageData();
  };

  const clearLogs = () => {
    logger.clear();
    setLogs([]);
  };

  useEffect(() => {
    loadLocalStorageData();
    loadSessionStorageData();
    loadMemStoreData();
    loadGlideStorageData();
    setLogs(logger.getLogs());

    // Update logs and storage data every second to capture new entries
    const interval = setInterval(() => {
      loadLocalStorageData();
      loadSessionStorageData();
      loadMemStoreData();
      loadGlideStorageData();
      setLogs(logger.getLogs());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <TransactionCheckoutHeader />
      <ModalBackButton onClick={onBack} />

      <div className="px-4 pt-8 mb-4">
        <SectionTitle title="Debug Information" />

        {/* Device Information Section */}
        <div className="mb-6">
          <p className="text-sm text-[--color-text-secondary] mb-2">
            Device Information
          </p>
          <div className="border border-[--color-modal-border] rounded-md p-3 mb-4">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Mobile Device
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {isMobile ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Disable Popups
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.popupsBlocked ? "Yes" : "No"}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    User Agent
                  </td>
                  <td className="py-2 text-[--color-text-primary] break-all">
                    <div className="max-w-full overflow-x-auto">
                      {navigator.userAgent}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Deposit Configuration Section */}
        <div className="mb-6">
          <p className="text-sm text-[--color-text-secondary] mb-2">
            Deposit Configuration
          </p>
          <div className="border border-[--color-modal-border] rounded-md p-3 mb-4">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    App Name
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.appName}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Mode
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.mode || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Chain
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.chain?.name || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Currency
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.currency?.symbol || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Amount
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.amount || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Recipient
                  </td>
                  <td className="py-2 text-[--color-text-primary] break-all">
                    <div className="max-w-full overflow-x-auto">
                      {depositConfig.recipient || "Not set"}
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Connected Wallet
                  </td>
                  <td className="py-2 text-[--color-text-primary] break-all">
                    <div className="max-w-full overflow-x-auto">
                      {depositConfig.connectedWalletAddress || "Not connected"}
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Project ID
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.glideConfig.projectId}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Can Switch Mode
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.canSwitchMode ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Prefer Gasless Payment
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.preferGaslessPayment ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Embed Mode
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.embed ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Auto Close on Success
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.autoCloseOnSuccess ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Whitelabel
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.whitelabel ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Debug Mode
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.debug ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Did Provide Local Storage
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.didProvideLocalStorage ? "Yes" : "No"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Default Token Denomination
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.defaultDepositTokenDenomination || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Widget Session ID
                  </td>
                  <td className="py-2 text-[--color-text-primary] break-all">
                    <div className="max-w-full overflow-x-auto">
                      {depositConfig.widgetSessionId || "Not set"}
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Payment Methods
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.paymentMethods.join(", ")}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Chain IDs
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.chainIds?.join(", ") || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Excluded Chain IDs
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.excludeChainIds?.join(", ") || "None"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Excluded Currency Tiers
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.excludeCurrencyTiers?.join(", ") || "None"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Excluded Funding Sources
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.excludeFundingSources?.join(", ") || "None"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Gas Refuel Amounts
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.gasRefuelAmountPerChain
                      ? JSON.stringify(depositConfig.gasRefuelAmountPerChain)
                      : "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Commission Rates
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.commissionRates
                      ? JSON.stringify(depositConfig.commissionRates)
                      : "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Available Chains
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.glideConfig.chains
                      .map((chain) => chain.name)
                      .join(", ")}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Withdraw Chains
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.withdrawChains
                      ?.map((chain) => chain.name)
                      .join(", ") || "Not set"}
                  </td>
                </tr>
                <tr className="border-b border-[--color-modal-border]">
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    EVM Transaction
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.evmTx
                      ? JSON.stringify(depositConfig.evmTx)
                      : "Not set"}
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium text-[--color-text-primary]">
                    Token Approval
                  </td>
                  <td className="py-2 text-[--color-text-primary]">
                    {depositConfig.tokenApproval
                      ? JSON.stringify(depositConfig.tokenApproval)
                      : "Not set"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Logs Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm text-[--color-text-secondary]">
              Application Logs
            </p>
            <button
              onClick={clearLogs}
              className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-gray-700"
            >
              Clear Logs
            </button>
          </div>
          <div className="border border-[--color-modal-border] rounded-md p-3 overflow-auto max-h-[200px] mb-4">
            {logs.length === 0 ? (
              <p className="text-sm py-2 text-[--color-text-secondary]">
                No logs available
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[--color-modal-border]">
                    <th className="text-left py-2 pr-4 font-medium w-24 text-[--color-text-primary]">
                      Time
                    </th>
                    <th className="text-left py-2 font-medium text-[--color-text-primary]">
                      Message
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr
                      key={index}
                      className="border-b border-[--color-modal-border] last:border-b-0"
                    >
                      <td className="py-2 pr-4 align-top whitespace-nowrap text-[--color-text-primary]">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </td>
                      <td className="py-2 align-top break-words text-[--color-text-primary]">
                        <div className="max-w-full overflow-x-auto">
                          {log.message}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center mb-2">
          <p className="text-sm text-[--color-text-secondary]">
            In-Memory Store
          </p>
          <button
            onClick={clearMemStore}
            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-gray-700"
          >
            Clear Memory Store
          </button>
        </div>

        {memStoreKeys.length === 0 ? (
          <p className="text-sm py-2 text-[--color-text-secondary]">
            No memory store items found
          </p>
        ) : (
          <div className="border border-[--color-modal-border] rounded-md p-3 overflow-auto max-h-[200px] mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[--color-modal-border]">
                  <th className="text-left py-2 pr-4 font-medium text-[--color-text-primary]">
                    Key
                  </th>
                  <th className="text-left py-2 font-medium text-[--color-text-primary]">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {memStoreKeys.map(({ key, value }) => (
                  <tr
                    key={key}
                    className="border-b border-[--color-modal-border] last:border-b-0"
                  >
                    <td className="py-2 pr-4 align-top break-all text-[--color-text-primary]">
                      {key}
                    </td>
                    <td className="py-2 align-top break-all text-[--color-text-primary]">
                      <div className="max-w-[260px] overflow-x-auto">
                        {value}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-between items-center mb-2">
          <p className="text-sm text-[--color-text-secondary]">
            Glide Local Storage
          </p>
          <button
            onClick={clearGlideStorage}
            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-gray-700"
          >
            Clear Glide Storage
          </button>
        </div>

        {glideStorageKeys.length === 0 ? (
          <p className="text-sm py-2 text-[--color-text-secondary]">
            No glide storage items found
          </p>
        ) : (
          <div className="border border-[--color-modal-border] rounded-md p-3 overflow-auto max-h-[200px] mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[--color-modal-border]">
                  <th className="text-left py-2 pr-4 font-medium text-[--color-text-primary]">
                    Key
                  </th>
                  <th className="text-left py-2 font-medium text-[--color-text-primary]">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {glideStorageKeys.map(({ key, value }) => (
                  <tr
                    key={key}
                    className="border-b border-[--color-modal-border] last:border-b-0"
                  >
                    <td className="py-2 pr-4 align-top break-all text-[--color-text-primary]">
                      {key}
                    </td>
                    <td className="py-2 align-top break-all text-[--color-text-primary]">
                      <div className="max-w-[260px] overflow-x-auto">
                        {value}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-between items-center mb-2">
          <p className="text-sm text-[--color-text-secondary]">
            Window Local Storage
          </p>
          <button
            onClick={clearLocalStorage}
            className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-gray-700"
          >
            Clear Local Storage
          </button>
        </div>

        {glideKeys.length === 0 ? (
          <p className="text-sm py-2 text-[--color-text-secondary]">
            No matching keys found
          </p>
        ) : (
          <div className="border border-[--color-modal-border] rounded-md p-3 overflow-auto max-h-[200px] mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[--color-modal-border]">
                  <th className="text-left py-2 pr-4 font-medium text-[--color-text-primary]">
                    Key
                  </th>
                  <th className="text-left py-2 font-medium text-[--color-text-primary]">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {glideKeys.map(({ key, value }) => (
                  <tr
                    key={key}
                    className="border-b border-[--color-modal-border] last:border-b-0"
                  >
                    <td className="py-2 pr-4 align-top break-all text-[--color-text-primary]">
                      {key}
                    </td>
                    <td className="py-2 align-top break-all text-[--color-text-primary]">
                      <div className="max-w-[260px] overflow-x-auto">
                        {value}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* SessionStorage Section */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm text-[--color-text-secondary]">
              Session Storage
            </p>
            <button
              onClick={clearSessionStorage}
              className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-gray-700"
            >
              Clear Session Storage
            </button>
          </div>
          <div className="border border-[--color-modal-border] rounded-md p-3 overflow-auto max-h-[200px]">
            {sessionKeys.length === 0 ? (
              <p className="text-sm py-2 text-[--color-text-secondary]">
                No session storage items found
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[--color-modal-border]">
                    <th className="text-left py-2 pr-4 font-medium text-[--color-text-primary]">
                      Key
                    </th>
                    <th className="text-left py-2 font-medium text-[--color-text-primary]">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sessionKeys.map(({ key, value }) => (
                    <tr
                      key={key}
                      className="border-b border-[--color-modal-border] last:border-b-0"
                    >
                      <td className="py-2 pr-4 align-top break-all text-[--color-text-primary]">
                        {key}
                      </td>
                      <td className="py-2 align-top break-all text-[--color-text-primary]">
                        <div className="max-w-[260px] overflow-x-auto">
                          {value}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
